import { useCallback, useMemo } from "react";
import { SelectAmongData } from "@vms/SelectAmong/SelectAmongContext";
import { RespondentType } from "@models/RespondentType";
import { useOnSites, useSegments } from "@core/CommonDataContext";
import { components } from "@api/api";
import { useCommonData } from "@api/useCommonData";

export type AmongPayload = components["schemas"]["FlavorTracker.Entities.DTO.Common.AmongDropDownBaseRequest"];

export function useGetAmongPayload(excludeDefaultRespondents = false) {
    const segments = useSegments();
    const onSites = useOnSites();

    const defaultRespondentTypes = useMemo(
        (): AmongPayload['respondentTypes'] => excludeDefaultRespondents ? [] : [RespondentType.TotalUS],
        [excludeDefaultRespondents]
    );

    return useCallback(
        (data: SelectAmongData): AmongPayload => {
            const isAnyOptionSelected= data.respondentTypes.length > 0 ||
                data.restaurants.length > 0 ||
                data.cStores.length > 0 ||
                data.supermarkets.length > 0 ||
                data.hotels.length > 0 ||
                data.brands.length > 0 ||
                data.segmentsAndOnSites.length > 0 ||
                data.deliveryServices.length > 0;

            return {
                respondentTypes: isAnyOptionSelected
                    ? data.respondentTypes as AmongPayload['respondentTypes']
                    : defaultRespondentTypes,
                chains: [
                    ...data.restaurants,
                    ...data.cStores,
                    ...data.supermarkets,
                    ...data.hotels,
                ] as AmongPayload['chains'],
                cpgBrands: data.brands,
                segments: data.segmentsAndOnSites.filter(
                    i => segments.some(ii => ii.id === i)
                ) as AmongPayload['segments'],
                onSites: data.segmentsAndOnSites.filter(
                    i => onSites.some(ii => ii.id === i)
                ) as AmongPayload['onSites'],
                deliveryServices: data.deliveryServices as AmongPayload['deliveryServices'],
            };
        },
        [
            segments,
            onSites,
            defaultRespondentTypes,
        ]
    );
}

export function useGetFromAmongPayload() {
    const {
        data: {
            restaurants = [],
            cStores = [],
            supermarkets = [],
            hotels = [],
        },
    } = useCommonData();

    return useCallback(
        (payload: AmongPayload, excludeDefaultRespondents = false): SelectAmongData => {
            const chains = payload.chains ?? [];

            const isOnlyTotalUsSelected = payload.respondentTypes?.length === 1 && payload.respondentTypes[0] === RespondentType.TotalUS;

            return {
                respondentTypes: isOnlyTotalUsSelected && excludeDefaultRespondents ? [] : (payload.respondentTypes ?? []),
                restaurants: chains.filter(i => restaurants.some(ii => ii.id === i)),
                cStores: chains.filter(i => cStores.some(ii => ii.id === i)),
                supermarkets: chains.filter(i => supermarkets.some(ii => ii.id === i)),
                hotels: chains.filter(i => hotels.some(ii => ii.id === i)),
                brands: payload.cpgBrands || [],
                segmentsAndOnSites: [
                    ...(payload.segments || []),
                    ...(payload.onSites || []),
                ],
                deliveryServices: payload.deliveryServices || [],
            };
        },
        [restaurants, cStores, supermarkets, hotels]
    );
}